import { AlertCircle, FileText, Image as ImageIcon, Loader2, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import type { PendingAttachment } from '@/hooks/useFileUploads'
import { cn } from '@/lib/utils'

interface AttachmentListProps {
  attachments: PendingAttachment[]
  onRemove: (clientId: string) => void
}

const IMAGE_EXT = new Set(['.png', '.jpg', '.jpeg', '.gif', '.webp'])

function getExtension(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot === -1 ? '' : name.slice(dot).toLowerCase()
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function AttachmentList({ attachments, onRemove }: AttachmentListProps) {
  if (attachments.length === 0) return null

  return (
    <div className="border-border flex w-full flex-wrap gap-2 border-b px-3 py-2">
      {attachments.map((att) => {
        const isImage = IMAGE_EXT.has(getExtension(att.file.name))
        const Icon = isImage ? ImageIcon : FileText
        return (
          <div
            key={att.clientId}
            title={att.status === 'error' ? att.errorMessage : att.file.name}
            className={cn(
              'bg-muted/40 flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs',
              att.status === 'error' && 'border-destructive/60 ring-destructive/20 ring-1'
            )}
          >
            {att.status === 'uploading' ? (
              <Loader2 className="text-muted-foreground size-3.5 animate-spin" />
            ) : att.status === 'error' ? (
              <AlertCircle className="text-destructive size-3.5" />
            ) : (
              <Icon className="text-muted-foreground size-3.5" />
            )}
            <span className="max-w-[12rem] truncate">{att.file.name}</span>
            <span className="text-muted-foreground text-[10px]">{formatSize(att.file.size)}</span>
            <Button
              type="button"
              variant="iconOnly"
              size="icon-sm"
              className="size-5"
              onClick={() => onRemove(att.clientId)}
              aria-label={`Remove ${att.file.name}`}
            >
              <X className="size-3" />
            </Button>
          </div>
        )
      })}
    </div>
  )
}
